import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { SectionBg } from "../components/SectionBg";
import { BG, waLink, bookingMessage } from "../lib/data";
import { Btn, Input, Select } from "../components/dash/Shell";
import { payWithRazorpay } from "../lib/razorpay";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

export default function Book() {
  const { lang, a } = useLang();
  const d = a.book;
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [payCfg, setPayCfg] = useState({ razorpay_enabled: false });
  const [form, setForm] = useState({ name: "", phone: "", address: "", service_id: "", date: "", time: "10:00", notes: "", payment_method: "cod" });
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    api.get("/services").then(({ data }) => {
      setServices(data);
      const pre = params.get("service");
      setForm((f) => ({ ...f, service_id: data.find((s) => s.id === pre)?.id || data[0]?.id || "" }));
    });
    api.get("/payments/config").then(({ data }) => setPayCfg(data));
  }, []);

  const service = useMemo(() => services.find((s) => s.id === form.service_id), [services, form.service_id]);
  const subtotal = service?.price || 0;
  const discount = coupon?.discount || 0;
  const total = Math.max(subtotal - discount, 0);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { setCoupon(null); }, [form.service_id]);

  const applyCoupon = async () => {
    if (!couponInput.trim() || !service) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput.trim(), service_id: service.id });
      setCoupon(data);
      toast.success(`${d.applied}: ${data.code} (−₹${data.discount})`);
    } catch (e) {
      setCoupon(null);
      toast.error(errMsg(e));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!service) return;
    setBusy(true);
    // Open the WhatsApp tab synchronously (inside the click gesture) so browsers don't block it;
    // it is pointed at the pre-filled booking message once the booking is saved.
    const waTab = form.payment_method === "cod" ? window.open("", "_blank") : null;
    try {
      const { data: booking } = await api.post("/bookings", { ...form, coupon_code: coupon?.code || null });
      if (form.payment_method === "online" && booking.total > 0) {
        await payWithRazorpay({ booking, keyId: payCfg.key_id });
      }
      if (waTab) waTab.location = waLink(bookingMessage(booking));
      navigate(`/booking/${booking.id}`, { state: { booking } });
    } catch (err) {
      waTab?.close();
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="relative min-h-screen overflow-hidden">
      <SectionBg src={BG.hero} opacity={0.3} />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-10 lg:py-16">
        <Link to="/" data-testid="book-back-home" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-gold">
          <ArrowLeft size={13} /> OGO Hygiene Solutions
        </Link>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold mt-8">{d.tag}</p>
        <h1 data-testid="book-title" className="font-display font-extrabold text-4xl sm:text-5xl text-white mt-3 leading-tight">{d.title}</h1>
        <p className="text-slate-400 text-base sm:text-lg mt-4 max-w-2xl">{d.sub}</p>

        {services.length === 0 ? (
          <p data-testid="book-no-services" className="mt-10 text-slate-400">{d.noService}</p>
        ) : (
          <form onSubmit={submit} className="grid lg:grid-cols-12 gap-8 mt-12">
            <div className="lg:col-span-7 rounded-3xl border border-white/10 bg-panel/80 backdrop-blur-xl p-6 sm:p-8 space-y-5">
              <Field label={d.service}>
                <Select data-testid="book-service-select" value={form.service_id} onChange={set("service_id")}>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>{s.name[lang]} — ₹{s.price}</option>
                  ))}
                </Select>
              </Field>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label={d.name}><Input data-testid="book-name-input" required minLength={2} value={form.name} onChange={set("name")} /></Field>
                <Field label={d.phone}><Input data-testid="book-phone-input" required type="tel" minLength={10} maxLength={15} value={form.phone} onChange={set("phone")} /></Field>
              </div>
              <Field label={d.address}><Input data-testid="book-address-input" required minLength={5} value={form.address} onChange={set("address")} /></Field>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label={d.date}><Input data-testid="book-date-input" required type="date" min={today} value={form.date} onChange={set("date")} /></Field>
                <Field label={d.time}>
                  <Select data-testid="book-time-select" value={form.time} onChange={set("time")}>
                    {d.slots.map((s) => <option key={s} value={s}>{s}</option>)}
                  </Select>
                </Field>
              </div>
              <Field label={d.notes}><Input data-testid="book-notes-input" value={form.notes} onChange={set("notes")} /></Field>
            </div>

            <aside className="lg:col-span-5 space-y-5">
              <div className="rounded-3xl border border-gold/30 bg-gradient-to-b from-gold/10 to-panel p-6 sm:p-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold flex items-center gap-2"><Tag size={13} /> {d.coupon}</p>
                {coupon ? (
                  <div data-testid="book-coupon-applied" className="mt-3 flex items-center justify-between rounded-xl border border-mint/40 bg-mint/10 px-4 py-3">
                    <span className="font-mono text-sm text-mint">{coupon.code} · −₹{coupon.discount}</span>
                    <button type="button" data-testid="book-coupon-remove" onClick={() => setCoupon(null)} className="text-slate-300 hover:text-white"><X size={16} /></button>
                  </div>
                ) : (
                  <div className="mt-3 flex gap-2">
                    <Input data-testid="book-coupon-input" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="OGO10" />
                    <Btn type="button" variant="ghost" data-testid="book-coupon-apply" onClick={applyCoupon}>{d.apply}</Btn>
                  </div>
                )}

                <dl className="mt-6 space-y-2.5 text-sm">
                  <div className="flex justify-between text-slate-300"><dt>{d.subtotal}</dt><dd data-testid="book-subtotal">₹{subtotal}</dd></div>
                  <div className="flex justify-between text-mint"><dt>{d.discount}</dt><dd data-testid="book-discount">−₹{discount}</dd></div>
                  <div className="flex justify-between font-display font-extrabold text-white text-2xl pt-3 border-t border-white/10"><dt>{d.total}</dt><dd data-testid="book-total">₹{total}</dd></div>
                </dl>
                <p className="text-[11px] text-slate-500 mt-2">{d.priceNote}</p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-panel/80 p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">{d.pay}</p>
                <div className="mt-3 space-y-2">
                  {[["cod", d.cod, true], ["online", d.online, payCfg.razorpay_enabled]].map(([val, label, enabled]) => (
                    <label key={val} className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer ${form.payment_method === val ? "border-gold/60 bg-gold/5" : "border-white/10"} ${!enabled ? "opacity-50 cursor-not-allowed" : ""}`}>
                      <input data-testid={`book-pay-${val}`} type="radio" name="pay" value={val} disabled={!enabled} checked={form.payment_method === val} onChange={set("payment_method")} className="accent-[#E6C280]" />
                      <span className="text-sm text-white">{label}</span>
                    </label>
                  ))}
                </div>
                {!payCfg.razorpay_enabled && <p className="text-[11px] text-slate-500 mt-3">{d.onlineOff}</p>}
                <Btn data-testid="book-submit-button" type="submit" disabled={busy} className="w-full mt-5 py-3.5">
                  {busy ? d.paying : d.submit}
                </Btn>
              </div>
            </aside>
          </form>
        )}
      </div>
    </div>
  );
}
